import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const MaterialStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const MATERIAL_SORT_FIELDS = [
  "materialCode",
  "productName",
  "createdAt",
];

const MATERIAL_SORT_COLUMN_MAP = Object.fromEntries(
  MATERIAL_SORT_FIELDS.map((field) => [field, field]),
);

const DEFAULT_SORT_BY = "createdAt";
const DEFAULT_SORT_ORDER = "desc";
const DEFAULT_VIEW_MODE = "grid";

function normalizeSortBy(value) {
  const field = String(value || "").trim();
  return MATERIAL_SORT_FIELDS.includes(field) ? field : DEFAULT_SORT_BY;
}

function normalizeSortOrder(value) {
  const order = String(value || "").trim().toLowerCase();
  return order === "asc" ? "asc" : "desc";
}

function normalizeViewMode(value) {
  return String(value || "").trim().toLowerCase() === "list" ? "list" : "grid";
}

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function extractMaterialsPayload(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return { materials: [], pagination: {}, sortBy: null, sortOrder: null };
  }

  if (Array.isArray(dataBlock)) {
    return {
      materials: dataBlock,
      pagination:
        root.pagination && typeof root.pagination === "object"
          ? root.pagination
          : {},
      sortBy: root.sortBy || null,
      sortOrder: root.sortOrder || null,
    };
  }

  let list =
    dataBlock.materials ||
    dataBlock.materialList ||
    dataBlock.items ||
    root.materials;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.materials || inner.materialList || inner.items;
    }
  }

  const pagination =
    (dataBlock && dataBlock.pagination) || root.pagination || {};

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  const total =
    typeof pagination.total === "number"
      ? pagination.total
      : typeof pagination.totalCount === "number"
        ? pagination.totalCount
        : typeof dataBlock.totalCount === "number"
          ? dataBlock.totalCount
          : typeof dataBlock.total === "number"
            ? dataBlock.total
            : undefined;

  return {
    materials: Array.isArray(list) ? list : [],
    pagination:
      pagination && typeof pagination === "object" && !Array.isArray(pagination)
        ? { ...pagination, ...(total != null ? { total } : {}) }
        : total != null
          ? { total }
          : {},
    sortBy: dataBlock.sortBy || root.sortBy || null,
    sortOrder: dataBlock.sortOrder || root.sortOrder || null,
  };
}

function extractCategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return [];
  }

  let list =
    dataBlock.categories ||
    dataBlock.categoryList ||
    dataBlock.items ||
    root.categories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.categories || inner.categoryList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return Array.isArray(list) ? list : [];
}

function extractSubcategoriesList(responseData) {
  const root = responseData;
  let dataBlock = root?.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }
  if (!dataBlock || typeof dataBlock !== "object") {
    return [];
  }

  let list =
    dataBlock.subcategories ||
    dataBlock.subcategoryList ||
    dataBlock.items ||
    root.subcategories;

  if (list == null && dataBlock.data != null) {
    const inner =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
    if (inner && typeof inner === "object") {
      list = inner.subcategories || inner.subcategoryList || inner.items;
    }
  }

  if (typeof list === "string") {
    list = parseJsonIfString(list);
  }

  return Array.isArray(list) ? list : [];
}

function extractMaterialCountsPayload(responseData) {
  const root = responseData || {};
  let dataBlock = root.data != null ? root.data : root;

  if (typeof dataBlock === "string") {
    dataBlock = parseJsonIfString(dataBlock);
  }

  if (dataBlock && typeof dataBlock === "object" && dataBlock.data != null) {
    dataBlock =
      typeof dataBlock.data === "string"
        ? parseJsonIfString(dataBlock.data)
        : dataBlock.data;
  }

  const counts =
    dataBlock && typeof dataBlock === "object" && !Array.isArray(dataBlock)
      ? dataBlock
      : {};

  const toCount = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return {
    materialCount: toCount(counts.materialCount),
  };
}

function getMaterialRecordId(material) {
  return material?._id || material?.id || "";
}

function materialCodeValue(material) {
  return (
    material?.materialCode ||
    material?.materialId ||
    material?.code ||
    "-"
  );
}

function materialNameValue(material) {
  return (
    material?.productName ||
    material?.materialName ||
    material?.materialDescription ||
    material?.description ||
    material?.name ||
    "-"
  );
}

function materialCategoryLabel(material) {
  if (material?.categoryName) return material.categoryName;
  if (material?.category?.categoryName) return material.category.categoryName;
  if (material?.categoryId) return material.categoryId;
  if (material?.category?.categoryId) return material.category.categoryId;
  return "-";
}

function materialSubcategoryLabel(material) {
  if (material?.subcategoryName) return material.subcategoryName;
  if (material?.subcategory?.subcategoryName) {
    return material.subcategory.subcategoryName;
  }
  if (material?.subcategoryId) return material.subcategoryId;
  if (material?.subcategory?.subcategoryId) {
    return material.subcategory.subcategoryId;
  }
  return "-";
}

function materialHsnValue(material) {
  return material?.hsnCode || material?.hsn || "-";
}

function materialUomValue(material) {
  return material?.uom || material?.unit || "-";
}

function materialStockCategoryValue(material) {
  return (
    material?.stockCategory ||
    material?.stockCategoryName ||
    "-"
  );
}

function MaterialImageGallery({ images = [], materialName, isGridView = true }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    setCurrentImageIndex(0);
    setImageErrors({});
  }, [images]);

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  if (!images || images.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 w-100">
        <i
          className="fa-solid fa-cubes"
          style={{ fontSize: "36px", color: "#b0bec5" }}
        />
      </div>
    );
  }

  const currentImage = images[currentImageIndex];
  const hasMultipleImages = images.length > 1;

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  if (!currentImage || imageErrors[currentImageIndex]) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 w-100">
        <i
          className="fa-solid fa-cubes"
          style={{ fontSize: "36px", color: "#b0bec5" }}
        />
      </div>
    );
  }

  return (
    <div className="position-relative h-100 w-100 d-flex align-items-center justify-content-center overflow-hidden">
      <img
        src={currentImage.url}
        alt={materialName || "Material"}
        onError={() => handleImageError(currentImageIndex)}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          borderRadius: isGridView ? "0" : "4px",
        }}
      />
      
      {hasMultipleImages && (
        <>
          {/* Navigation Arrows */}
          <button
            className="btn btn-sm btn-dark position-absolute"
            style={{
              left: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.7,
              padding: "6px 10px",
              fontSize: "12px",
              zIndex: 2,
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handlePrevImage}
            title="Previous image"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          
          <button
            className="btn btn-sm btn-dark position-absolute"
            style={{
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              opacity: 0.7,
              padding: "6px 10px",
              fontSize: "12px",
              zIndex: 2,
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handleNextImage}
            title="Next image"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>

          {/* Image Counter */}
          <div
            className="position-absolute bg-dark text-white px-2 py-1"
            style={{
              bottom: "8px",
              right: "8px",
              fontSize: "11px",
              borderRadius: "12px",
              opacity: 0.85,
              zIndex: 2,
              fontWeight: "600",
            }}
          >
            {currentImageIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
}

function materialUserName(user) {
  if (!user) return "-";
  if (typeof user === "string") return user.trim() || "-";
  return user.name || user.userName || "-";
}

function renderMaterialStatus(status) {
  const value = String(status || "").trim();
  if (!value) return "-";
  const normalized = value.toLowerCase();
  const badgeClass =
    normalized === "active"
      ? "badge-success"
      : normalized === "inactive"
        ? "badge-danger"
        : "badge-secondary";
  return (
    <span
      className={`badge ${badgeClass}`}
      style={{ fontSize: "90%", fontWeight: 550 }}
    >
      {value}
    </span>
  );
}

const Material = () => {
  const { canCreate, canView, canUpdate, canDelete } = useModuleAccess(
    "material_management",
  );
  const savedFilters = JSON.parse(
    sessionStorage.getItem("materialFilters") || "{}",
  );

  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(savedFilters.searchQuery || "");
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [filterCategoryId, setFilterCategoryId] = useState(
    savedFilters.filterCategoryId || "",
  );
  const [activeFilterCategoryId, setActiveFilterCategoryId] = useState(
    savedFilters.activeFilterCategoryId || "",
  );
  const [filterSubcategoryId, setFilterSubcategoryId] = useState(
    savedFilters.filterSubcategoryId || "",
  );
  const [activeFilterSubcategoryId, setActiveFilterSubcategoryId] = useState(
    savedFilters.activeFilterSubcategoryId || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState(
    normalizeSortBy(savedFilters.sortBy || DEFAULT_SORT_BY),
  );
  const [sortOrder, setSortOrder] = useState(
    normalizeSortOrder(savedFilters.sortOrder || DEFAULT_SORT_ORDER),
  );
  const [viewMode, setViewMode] = useState(
    normalizeViewMode(savedFilters.viewMode || DEFAULT_VIEW_MODE),
  );
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [isLoadingCategoryOptions, setIsLoadingCategoryOptions] =
    useState(false);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [isLoadingSubcategoryOptions, setIsLoadingSubcategoryOptions] =
    useState(false);
  const [isMaterialCountLoading, setIsMaterialCountLoading] = useState(true);
  const [materialCount, setMaterialCount] = useState({
    materialCount: 0,
  });
  const [deleteMaterialId, setDeleteMaterialId] = useState(null);
  const [deleteMaterialLabel, setDeleteMaterialLabel] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMaterialCounts = useCallback(async () => {
    setIsMaterialCountLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "material-counts",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        setMaterialCount(extractMaterialCountsPayload(responseData));
      } else {
        setMaterialCount({ materialCount: 0 });
      }
    } catch (error) {
      console.error("Material counts error:", error);
      setMaterialCount({ materialCount: 0 });
    } finally {
      setIsMaterialCountLoading(false);
    }
  }, []);

  const fetchCategoryOptions = async () => {
    setIsLoadingCategoryOptions(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-categories",
        page: 1,
        limit: 500,
        sortBy: "categoryName",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.categories;

      if (!Array.isArray(list)) {
        list = extractCategoriesList(responseData);
      }

      setCategoryOptions(
        isApiSuccess(responseData) && Array.isArray(list) ? list : [],
      );
    } catch (error) {
      console.error("Fetch category options error:", error);
      setCategoryOptions([]);
    } finally {
      setIsLoadingCategoryOptions(false);
    }
  };

  const fetchSubcategoryOptions = useCallback(async (categoryId) => {
    const trimmedCategoryId = String(categoryId || "").trim();
    if (!trimmedCategoryId) {
      setSubcategoryOptions([]);
      return;
    }

    setIsLoadingSubcategoryOptions(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-subcategories",
        page: 1,
        limit: 500,
        categoryId: trimmedCategoryId,
        sortBy: "subcategoryName",
        sortOrder: "asc",
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      let list = responseData?.data?.subcategories;

      if (!Array.isArray(list)) {
        list = extractSubcategoriesList(responseData);
      }

      setSubcategoryOptions(
        isApiSuccess(responseData) && Array.isArray(list) ? list : [],
      );
    } catch (error) {
      console.error("Fetch subcategory options error:", error);
      setSubcategoryOptions([]);
    } finally {
      setIsLoadingSubcategoryOptions(false);
    }
  }, []);

  const fetchMaterials = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-all-material",
        page: currentPage,
        limit: itemsPerPage,
        sortBy: normalizeSortBy(sortBy),
        sortOrder: normalizeSortOrder(sortOrder),
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (activeFilterCategoryId) {
        payload.categoryId = activeFilterCategoryId;
      }

      if (activeFilterSubcategoryId) {
        payload.subcategoryId = activeFilterSubcategoryId;
      }

      const response = await axios.post(API_URL, payload);
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const {
        materials: list,
        pagination,
        sortBy: responseSortBy,
        sortOrder: responseSortOrder,
      } = extractMaterialsPayload(responseData);

      const ok =
        isApiSuccess(responseData) || (Array.isArray(list) && list.length > 0);

      if (ok) {
        setMaterials(list);
        const total =
          typeof pagination.total === "number"
            ? pagination.total
            : typeof pagination.totalCount === "number"
              ? pagination.totalCount
              : list.length;
        setTotalCount(total);

        if (responseSortBy && MATERIAL_SORT_FIELDS.includes(responseSortBy)) {
          setSortBy(responseSortBy);
        }
        if (responseSortOrder === "asc" || responseSortOrder === "desc") {
          setSortOrder(responseSortOrder);
        }
      } else {
        toast.error(responseData?.message || "Failed to fetch materials", {
          autoClose: 2000,
        });
        setMaterials([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch materials error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      const msg =
        typeof errData === "object" && errData?.message
          ? errData.message
          : "An error occurred while fetching materials";
      toast.error(msg, { autoClose: 2000 });
      setMaterials([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeFilterCategoryId,
    activeFilterSubcategoryId,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchMaterialCounts();
  }, [fetchMaterialCounts]);

  useEffect(() => {
    fetchCategoryOptions();
  }, []);

  useEffect(() => {
    fetchSubcategoryOptions(activeFilterCategoryId);
  }, [activeFilterCategoryId, fetchSubcategoryOptions]);

  useEffect(() => {
    sessionStorage.setItem(
      "materialFilters",
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        filterCategoryId,
        activeFilterCategoryId,
        filterSubcategoryId,
        activeFilterSubcategoryId,
        currentPage,
        itemsPerPage,
        sortBy,
        sortOrder,
        viewMode,
      }),
    );
  }, [
    searchQuery,
    activeSearchQuery,
    filterCategoryId,
    activeFilterCategoryId,
    filterSubcategoryId,
    activeFilterSubcategoryId,
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    viewMode,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    materials.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);
  const hasActiveFilters = Boolean(
    activeSearchQuery || activeFilterCategoryId || activeFilterSubcategoryId,
  );

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const tp = totalPages;

    if (tp <= maxVisiblePages) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis");
      pages.push(tp);
    }
    return pages;
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setFilterCategoryId("");
    setActiveFilterCategoryId("");
    setFilterSubcategoryId("");
    setActiveFilterSubcategoryId("");
    setSubcategoryOptions([]);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";
    setActiveSearchQuery(trimmedQuery);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleCategoryFilterChange = (e) => {
    const value = e.target.value;
    setFilterCategoryId(value);
    setActiveFilterCategoryId(value);
    setFilterSubcategoryId("");
    setActiveFilterSubcategoryId("");
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSubcategoryFilterChange = (e) => {
    const value = e.target.value;
    setFilterSubcategoryId(value);
    setActiveFilterSubcategoryId(value);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSort = (column) => {
    const apiField = MATERIAL_SORT_COLUMN_MAP[column];
    if (!apiField) return;

    if (sortBy === apiField) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }
    if (currentPage !== 1) setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = MATERIAL_SORT_COLUMN_MAP[column];
    if (!apiField || sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort ml-1"
          style={{ opacity: 0.35, fontSize: "11px" }}
        />
      );
    }
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}
        style={{ fontSize: "11px" }}
      />
    );
  };

  const renderSortableHeader = (column, label, style = {}) => (
    <th
      style={{ whiteSpace: "nowrap", cursor: "pointer", ...style }}
      onClick={() => handleSort(column)}
    >
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        {label}
        {getSortIcon(column)}
      </span>
    </th>
  );

  const renderCategoryFilterOptions = () =>
    categoryOptions.map((category) => {
      const displayId = category.categoryId || "";
      const name = category.categoryName || "-";
      return (
        <option key={displayId || category._id || category.id} value={displayId}>
          {displayId} - {name}
        </option>
      );
    });

  const renderSubcategoryFilterOptions = () =>
    subcategoryOptions.map((subcategory) => {
      const displayId = subcategory.subcategoryId || "";
      const name = subcategory.subcategoryName || "-";
      return (
        <option
          key={displayId || subcategory._id || subcategory.id}
          value={displayId}
        >
          {displayId} - {name}
        </option>
      );
    });

  const handleDeleteClick = (material) => {
    const materialId = getMaterialRecordId(material);
    if (!materialId) {
      toast.error("Material ID is missing", { autoClose: 2000 });
      return;
    }

    const code = materialCodeValue(material);
    const name = materialNameValue(material);
    const label =
      code !== "-" && name !== "-"
        ? `${code} - ${name}`
        : code !== "-"
          ? code
          : name;

    setDeleteMaterialId(materialId);
    setDeleteMaterialLabel(label);
  };

  const handleDelete = async () => {
    if (!deleteMaterialId) return;

    const updatedBy = localStorage.getItem("id");
    if (!updatedBy) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: "delete-material",
        id: deleteMaterialId,
        updatedBy,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message || "Material deleted successfully",
          { autoClose: 2000 },
        );
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }
        setDeleteMaterialId(null);
        setDeleteMaterialLabel("");
        if (materials.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchMaterials();
        }
        fetchMaterialCounts();
      } else {
        toast.error(responseData?.message || "Failed to delete material", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete material error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while deleting material",
        { autoClose: 2000 },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const renderActionButtons = (material) => {
    const id = getMaterialRecordId(material);
    if (!id) return null;
    return (
      <div
        align="center"
        style={{ whiteSpace: "nowrap" }}
        className="d-flex justify-content-center align-items-center"
      >
        {canView && (
          <Link
            to={`/materialView/${id}`}
            className="crsr_pntr action-icon-link mr-2"
            title="View"
          >
            <i className="fa-solid fa-eye" />
          </Link>
        )}
        {canUpdate && (
          <Link
            to={`/materialEdit/${id}`}
            className="crsr_pntr action-icon-link mr-2"
            title="Edit"
          >
            <i className="fa-solid fa-pen" />
          </Link>
        )}
        {canDelete && (
          <a
            href="#modaldelete"
            className="crsr_pntr action-icon-link"
            data-toggle="modal"
            data-target="#modaldelete"
            title="Delete"
            onClick={() => handleDeleteClick(material)}
          >
            <i className="fa-solid fa-trash-can" />
          </a>
        )}
      </div>
    );
  };

  const renderPagination = () => (
    <div className="supply-pagination-container">
      <div className="pagination-wrapper">
        <div className="pagination-info">
          <label className="mb-0 mr-2" style={{ fontSize: "13px" }}>
            Show:
          </label>
          <select
            className="form-control form-control-sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <span className="ml-2" style={{ fontSize: "13px" }}>
            entries
          </span>
          <span className="ml-3 text-muted" style={{ fontSize: "13px" }}>
            {startItem}–{endItem} of {totalCount}
          </span>
        </div>

        <nav className="pagination-nav">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First Page"
              >
                <i className="fa-solid fa-angle-double-left" />
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <i className="fa-solid fa-angle-left" />
              </button>
            </li>

            {getPageNumbers().map((page, index) =>
              page === "ellipsis" ? (
                <li
                  key={`ellipsis-${index}`}
                  className="page-item disabled"
                >
                  <span className="page-link">...</span>
                </li>
              ) : (
                <li
                  key={page}
                  className={`page-item ${currentPage === page ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                </li>
              ),
            )}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <i className="fa-solid fa-angle-right" />
              </button>
            </li>
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <i className="fa-solid fa-angle-double-right" />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );

  const renderGridView = () => (
    <div>
      <div className="row">
        {materials.map((material, index) => {
          const id = getMaterialRecordId(material);
          const images = Array.isArray(material.images) ? material.images : [];
          return (
            <div
              key={id || index}
              className="col-xl-3 col-lg-4 col-md-6 col-sm-6 mb-3"
            >
              <div
                className="card h-100"
                style={{
                  borderRadius: "10px",
                  border: "1px solid #e3e8ee",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}
              >
                <div
                  style={{
                    height: "160px",
                    backgroundColor: "#f4f7f9",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                    overflow: "hidden",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "8px",
                  }}
                >
                  <MaterialImageGallery
                    images={images}
                    materialName={materialNameValue(material)}
                    isGridView={true}
                  />
                </div>
                <div className="card-body" style={{ padding: "14px 16px" }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div
                      style={{
                        color: "#1199B2",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {materialCodeValue(material)}
                    </div>
                    {renderMaterialStatus(material.status || "Active")}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212529",
                      marginBottom: "8px",
                      minHeight: "40px",
                      wordBreak: "break-word",
                    }}
                  >
                    {materialNameValue(material)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>Category:</strong> {materialCategoryLabel(material)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>Subcategory:</strong>{" "}
                    {materialSubcategoryLabel(material)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "4px",
                    }}
                  >
                    <strong>Stock Category:</strong>{" "}
                    {materialStockCategoryValue(material)}
                  </div>
                  <div
                    className="d-flex justify-content-between"
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginBottom: "10px",
                    }}
                  >
                    <span>
                      <strong>HSN:</strong> {materialHsnValue(material)}
                    </span>
                    <span>
                      <strong>UOM:</strong> {materialUomValue(material)}
                    </span>
                  </div>
                  {images.length > 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6c757d",
                        marginBottom: "10px",
                      }}
                    >
                      <strong>Images:</strong> {images.length}
                    </div>
                  )}
                  <div className="d-flex justify-content-end">
                    {renderActionButtons(material)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {renderPagination()}
    </div>
  );

  const renderListView = () => (
    <div className="supply-table-container">
      <div className="table-responsive">
        <table
          id="materials"
          width="100%"
          className="table table-striped table-bordered txt_lft"
        >
          <thead>
            <tr>
              <th
                style={{
                  whiteSpace: "nowrap",
                  width: "40px",
                  minWidth: "40px",
                }}
              >
                S.No
              </th>
              <th
                style={{
                  whiteSpace: "nowrap",
                  width: "70px",
                  minWidth: "70px",
                }}
              >
                Image
              </th>
              {renderSortableHeader("materialCode", "Material Code", {
                minWidth: "120px",
              })}
              {renderSortableHeader("productName", "Material Name", {
                minWidth: "160px",
              })}
              <th style={{ minWidth: "120px" }}>Category</th>
              <th style={{ minWidth: "120px" }}>Subcategory</th>
              <th style={{ minWidth: "120px" }}>Stock Category</th>
              <th style={{ minWidth: "80px" }}>HSN</th>
              <th style={{ minWidth: "70px" }}>UOM</th>
              <th style={{ minWidth: "90px" }}>Status</th>
              <th style={{ minWidth: "120px" }}>Created By</th>
              <th style={{ width: "80px", minWidth: "80px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((material, index) => {
              const id = getMaterialRecordId(material);
              const images = Array.isArray(material.images) ? material.images : [];
              return (
                <tr key={id || index}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "6px",
                        overflow: "hidden",
                        backgroundColor: "#f4f7f9",
                        border: "1px solid #e3e8ee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px",
                      }}
                    >
                      <MaterialImageGallery
                        images={images}
                        materialName={materialNameValue(material)}
                        isGridView={false}
                      />
                    </div>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialCodeValue(material)}
                  </td>
                  <td
                    style={{
                      maxWidth: "200px",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {materialNameValue(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialCategoryLabel(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialSubcategoryLabel(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialStockCategoryValue(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialHsnValue(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialUomValue(material)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {renderMaterialStatus(material.status)}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {materialUserName(material.createdBy)}
                  </td>
                  <td>{renderActionButtons(material)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isLoading && materials.length > 0 && renderPagination()}
    </div>
  );

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Materials</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/materialCreate" className="btn btn-blue">
                  Create Material
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isMaterialCountLoading ? (
                  <MaterialStatCardSkeleton />
                ) : (
                  <div className="col-xl-auto col-lg-auto col-md-4 mb-2 stat-card-col">
                    <div className="card supplier-stat-card">
                      <div className="card-body">
                        <div
                          className="stat-title"
                          style={{
                            color: "#1F618D",
                            fontSize: "13px",
                            fontWeight: "600",
                            marginBottom: "8px",
                          }}
                        >
                          Total Materials
                        </div>
                        <div
                          className="stat-value"
                          style={{
                            color: "#2980B9",
                            fontSize: "22px",
                            fontWeight: "700",
                          }}
                        >
                          {materialCount.materialCount}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-3">
                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search materials..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Category
                        </label>
                        <select
                          className="form-control"
                          value={filterCategoryId}
                          onChange={handleCategoryFilterChange}
                          disabled={isLoadingCategoryOptions}
                        >
                          <option value="">All Categories</option>
                          {renderCategoryFilterOptions()}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Subcategory
                        </label>
                        <select
                          className="form-control"
                          value={filterSubcategoryId}
                          onChange={handleSubcategoryFilterChange}
                          disabled={
                            !filterCategoryId || isLoadingSubcategoryOptions
                          }
                        >
                          <option value="">
                            {filterCategoryId
                              ? "All Subcategories"
                              : "Select category first"}
                          </option>
                          {renderSubcategoryFilterOptions()}
                        </select>
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1 d-block"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          View
                        </label>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              viewMode === "grid"
                                ? "btn-info"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setViewMode("grid")}
                            title="Grid view"
                            style={{
                              backgroundColor:
                                viewMode === "grid" ? "#1199B2" : undefined,
                              borderColor:
                                viewMode === "grid" ? "#1199B2" : undefined,
                              color: viewMode === "grid" ? "#fff" : undefined,
                            }}
                          >
                            <i className="fa-solid fa-th mr-1" />
                            Grid
                          </button>
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              viewMode === "list"
                                ? "btn-info"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => setViewMode("list")}
                            title="List view"
                            style={{
                              backgroundColor:
                                viewMode === "list" ? "#1199B2" : undefined,
                              borderColor:
                                viewMode === "list" ? "#1199B2" : undefined,
                              color: viewMode === "list" ? "#fff" : undefined,
                            }}
                          >
                            <i className="fa-solid fa-list mr-1" />
                            List
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : materials.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no materials yet.
                  <br />
                  {canCreate && (
                    <div className="mt-3">
                      <Link to="/materialCreate" className="btn btn-blue">
                        Create Material
                      </Link>
                    </div>
                  )}
                </div>
              ) : materials.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No materials found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : viewMode === "grid" ? (
                renderGridView()
              ) : (
                renderListView()
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete Material
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
                disabled={isDeleting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete
                <br />
                <span className="fwt_600">{deleteMaterialLabel || "-"}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Material;
